import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 80px;
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
    gap: 10px;
  }
`;

const CarouselContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 8%,
    black 92%,
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 8%,
    black 92%,
    transparent 100%
  );
`;

const CarouselWrapper = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  will-change: transform;
`;

const PartnerCard = styled(motion.div)`
  background: none;
  border: 1px solid rgba(135,206,235,0.3);
  padding: 30px 20px;
  border-radius: 15px;
  text-align: center;
  height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  will-change: transform, opacity;
  flex-shrink: 0;
  margin-right: 20px;
  
  /* Desktop: 6 cards per row */
  @media (min-width: 769px) {
    width: calc((100% - 100px) / 6);
    
    &:hover {
      transform: translateY(-5px);
      border-color: rgba(135,206,235,0.5);
    }
  }
  
  /* Mobile: 3 cards per row */
  @media (max-width: 768px) {
    width: calc((100% - 40px) / 3);
    margin-right: 10px;
  }
`;

const PartnerLogo = styled.div`
  width: 60px;
  height: 60px;
  background: none;
  border: none;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: #87ceeb;
  font-weight: 700;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    width: 50px;
    height: 50px;
  }
`;

const NavigationButton = styled.button`
  background: rgba(135,206,235,0.15);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(135,206,235,0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #87ceeb;
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  
  &:hover {
    background: rgba(135,206,235,0.25);
    border-color: rgba(135,206,235,0.4);
    backdrop-filter: blur(16px);
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
`;

const TechnologyPartnersCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  
  const partners = [
    { name: 'AWS', logo: 'AWS' },
    { name: 'Google Cloud', logo: 'GCP' },
    { name: 'Microsoft Azure', logo: 'Azure' },
    { name: 'MongoDB', logo: 'Mongo' },
    { name: 'React', logo: 'React' },
    { name: 'Node.js', logo: 'Node' },
    { name: 'Docker', logo: 'Docker' },
    { name: 'Kubernetes', logo: 'K8s' },
    { name: 'GitHub', logo: 'GitHub' },
    { name: 'GitLab', logo: 'GitLab' },
    { name: 'Jenkins', logo: 'Jenkins' },
    { name: 'Terraform', logo: 'TF' }
  ];

  // Create infinite loop by duplicating cards
  const extendedPartners = [...partners, ...partners, ...partners];
  const startIndex = partners.length; // Start from middle set

  // Calculate items per view based on screen size
  const getItemsPerView = () => {
    if (window.innerWidth <= 768) return 3; // Mobile
    return 6; // Desktop
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize currentIndex to startIndex
  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex]);

  // Auto-slide functionality with infinite loop
  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const next = prev + 1;
          // Reset to start of middle set when reaching end
          if (next >= startIndex + partners.length) {
            setTimeout(() => setCurrentIndex(startIndex), 0);
            return startIndex;
          }
          return next;
        });
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, startIndex, partners.length]);

  const goToPrevious = () => {
    setCurrentIndex(prev => {
      const next = prev - 1;
      // Reset to end of middle set when going before start
      if (next < startIndex) {
        setTimeout(() => setCurrentIndex(startIndex + partners.length - 1), 0);
        return startIndex + partners.length - 1;
      }
      return next;
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const goToNext = () => {
    setCurrentIndex(prev => {
      const next = prev + 1;
      // Reset to start of middle set when reaching end
      if (next >= startIndex + partners.length) {
        setTimeout(() => setCurrentIndex(startIndex), 0);
        return startIndex;
      }
      return next;
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  return (
    <CarouselContainer>
      <NavigationButton onClick={goToPrevious}>
        <ChevronLeft size={20} />
      </NavigationButton>
      
      <CarouselContent>
        <CarouselWrapper
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
          }}
        >
          {extendedPartners.map((partner, index) => (
            <PartnerCard
              key={index}
              initial={{ opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.12, 
                ease: "easeOut", 
                delay: (index % itemsPerView) * 0.02 
              }}
            >
              <div style={{ 
                color: '#87ceeb', 
                fontSize: window.innerWidth <= 768 ? '0.8rem' : '1rem', 
                fontWeight: '700',
                textAlign: 'center'
              }}>
                {partner.logo}
              </div>
            </PartnerCard>
          ))}
        </CarouselWrapper>
      </CarouselContent>
      
      <NavigationButton onClick={goToNext}>
        <ChevronRight size={20} />
      </NavigationButton>
    </CarouselContainer>
  );
};

export default TechnologyPartnersCarousel;